// Stop something, and say why.
//
// One dialog for every "cancel with a reason" in the product — work items,
// expense reports, payroll runs. They are the same act with different nouns,
// and a person who has learned one should not have to learn the others.
//
// The reason is a CODE, not prose: "why are we cancelling so many of these" is
// the question that makes the record worth keeping, and free text cannot be
// grouped. The note carries the detail a code cannot.
//
// Whether a reason is REQUIRED is the caller's decision, and it follows one
// rule across all three: has anyone else seen this yet? A private draft may be
// dropped without justification — demanding prose for a misclick is how a log
// fills up with "x". Once it is in someone's queue, an explanation is owed.
//
// "Other" is the single exception in both modes: it says nothing on its own, so
// it is the only choice that asks for words.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';

export interface CancelReasonOption {
  code: string;
  label: string;
}

export interface CancelWithReasonDialogProps {
  open: boolean;
  busy?: boolean;
  /** Ordered exactly as the server lists them — the first option is the
   *  commonest honest reason, and that ordering is deliberate. */
  reasons: CancelReasonOption[];
  /** Mirrors the server's own "has anyone seen it" test. When true there is no
   *  "Not given" option and the action stays disabled until a reason is picked. */
  reasonRequired?: boolean;
  title: string;
  description: string;
  /** Label for the destructive action, e.g. "Cancel report". Named for the
   *  thing being stopped, never a bare "Confirm". */
  confirmLabel: string;
  /** Label for the way out. Defaults to "Keep it". */
  keepLabel?: string;
  notePlaceholder?: string;
  /** Extra, caller-owned controls under the note — e.g. "also cancel the open
   *  sub-items". The caller owns their state and resets it on open. */
  children?: React.ReactNode;
  onClose: () => void;
  onConfirm: (opts: { reason?: string; note?: string }) => void;
}

export const CancelWithReasonDialog: React.FC<CancelWithReasonDialogProps> = ({
  open,
  busy,
  reasons,
  reasonRequired,
  title,
  description,
  confirmLabel,
  keepLabel,
  notePlaceholder,
  children,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('common');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  // Reopening must not inherit the last attempt's answer: the next cancellation
  // is a different one, and a pre-filled reason is a wrong reason waiting to be
  // submitted without being read.
  useEffect(() => {
    if (open) {
      setReason('');
      setNote('');
    }
  }, [open]);

  // Mirrors the server's validation exactly, so the button is disabled for the
  // same cases the backend would refuse — and when it does refuse, that message
  // is shown verbatim rather than being second-guessed here.
  const needsNote = reason === 'other' && !note.trim();
  const needsReason = Boolean(reasonRequired) && !reason;
  const blocked = Boolean(busy) || needsNote || needsReason;

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="cancel-reason" className="text-xs">
              {t('cancel.reason', { defaultValue: 'Reason' })}
              {reasonRequired
                ? ' *'
                : <> <span className="text-muted-foreground">{t('cancel.optional', { defaultValue: '(optional)' })}</span></>}
            </Label>
            <select
              id="cancel-reason"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              aria-invalid={needsReason || undefined}
            >
              <option value="">
                {reasonRequired
                  ? t('cancel.choose', { defaultValue: 'Choose a reason…' })
                  : t('cancel.notGiven', { defaultValue: 'Not given' })}
              </option>
              {reasons.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="cancel-note" className="text-xs">
              {t('cancel.note', { defaultValue: 'Note' })}
              {reason === 'other' ? ' *' : <> {t('cancel.optional', { defaultValue: '(optional)' })}</>}
            </Label>
            <Input
              id="cancel-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={notePlaceholder}
              disabled={busy}
              aria-invalid={needsNote || undefined}
            />
            {needsNote && (
              <p className="mt-1 text-xs text-danger-strong">
                {t('cancel.noteRequired', {
                  defaultValue: '“Other” needs a short note saying what happened.',
                })}
              </p>
            )}
          </div>
          {children}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={busy}>
            {keepLabel ?? t('cancel.keep', { defaultValue: 'Keep it' })}
          </Button>
          <Button
            variant="destructive"
            disabled={blocked}
            onClick={() => onConfirm({ reason: reason || undefined, note: note.trim() || undefined })}
          >
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelWithReasonDialog;
